import React from 'react';
import Select from 'react-select';

const C = {
  dark: "#1a3328", mid: "#2d5a3d", bright: "#3d8c5e",
  light: "#e8f5ee", border: "#d8e8dc",
  white: "#ffffff", textDk: "#1a2e22", textGr: "#6c757d",
  red: "#c62828",
};

export const APP_METHODS_LIST = [
  "Spraying", "Drip Irrigation", "Soil Application",
  "Seed Treatment", "Foliar Spray", "Fertigation",
];

export const emptySolution = () => ({
  solutionName: '',
  products: [{ productId: '', productName: '', dose: '', applicationMethod: '' }],
});

const thSt = {
  padding: '0.5rem 0.75rem', fontSize: '0.7rem', fontWeight: 700,
  color: '#fff', textAlign: 'left', letterSpacing: '0.05em', whiteSpace: 'nowrap',
};
const tdSt = { padding: '0.45rem 0.6rem', verticalAlign: 'middle' };

const selectSt = {
  control: (b, s) => ({
    ...b, minHeight: 34, borderRadius: 7, fontSize: '0.83rem',
    borderColor: s.isFocused ? C.bright : C.border, boxShadow: 'none',
    '&:hover': { borderColor: C.bright },
  }),
  option: (b, s) => ({
    ...b, fontSize: '0.82rem',
    backgroundColor: s.isSelected ? C.mid : s.isFocused ? C.light : '#fff',
    color: s.isSelected ? '#fff' : C.textDk,
  }),
  placeholder: (b) => ({ ...b, fontSize: '0.82rem', color: '#aab8b0' }),
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  valueContainer: (b) => ({ ...b, padding: '0 8px' }),
};

const SolutionsEditor = ({ solutions, onChange, allProducts, readOnly }) => {
  const list = solutions || [];

  const productOpts = (allProducts || []).map(p => ({
    value: String(p._id),
    label: p.product || p.productName || p.name || '',
  }));

  const updateSolution = (sIdx, patch) =>
    onChange(list.map((s, i) => i === sIdx ? { ...s, ...patch } : s));

  const updateProduct = (sIdx, pIdx, patch) =>
    onChange(list.map((s, i) => i !== sIdx ? s : {
      ...s,
      products: s.products.map((p, j) => j === pIdx ? { ...p, ...patch } : p),
    }));

  const addProduct = (sIdx) =>
    onChange(list.map((s, i) => i !== sIdx ? s : {
      ...s,
      products: [...s.products, { productId: '', productName: '', dose: '', applicationMethod: '' }],
    }));

  const removeProduct = (sIdx, pIdx) =>
    onChange(list.map((s, i) => i !== sIdx ? s : {
      ...s,
      products: s.products.filter((_, j) => j !== pIdx),
    }));

  const addSolution = () => onChange([...list, emptySolution()]);
  const removeSolution = (idx) => onChange(list.filter((_, i) => i !== idx));

  // ── View (read-only) ─────────────────────────────────────────────────────
  if (readOnly) {
    if (list.length === 0)
      return <div style={{ fontSize: '0.85rem', color: '#aab8b0', padding: '4px 0' }}>—</div>;
    return (
      <div>
        {list.map((sol, i) => (
          <div key={i} style={{ border: `1.5px solid ${C.border}`, borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
            <div style={{ backgroundColor: C.light, padding: '0.55rem 1rem', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontWeight: 700, fontSize: '0.9rem', color: C.mid }}>
                Solution {i + 1}{sol.solutionName ? ` — ${sol.solutionName}` : ''}
              </span>
            </div>
            <div style={{ padding: '0.65rem 1rem' }}>
              {(sol.products || []).length === 0 ? (
                <div style={{ fontSize: '0.82rem', color: '#aab8b0' }}>No products added</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f1f7f3' }}>
                      <th style={{ ...thSt, color: C.mid, width: 32 }}>#</th>
                      <th style={{ ...thSt, color: C.mid }}>Product Name</th>
                      <th style={{ ...thSt, color: C.mid, width: 140 }}>Dose</th>
                      <th style={{ ...thSt, color: C.mid, width: 170 }}>Application Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sol.products.map((p, j) => (
                      <tr key={j} style={{ borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ ...tdSt, color: C.textGr, fontWeight: 600 }}>{j + 1}</td>
                        <td style={{ ...tdSt, fontWeight: 600, color: C.textDk }}>{p.productName || '—'}</td>
                        <td style={{ ...tdSt, color: C.textGr }}>{p.dose || '—'}</td>
                        <td style={{ ...tdSt, color: C.textGr }}>{p.applicationMethod || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ── Edit mode ────────────────────────────────────────────────────────────
  return (
    <div>
      {list.map((sol, sIdx) => (
        <div key={sIdx} style={{ border: `1.5px solid ${C.border}`, borderRadius: 12, marginBottom: 14, overflow: 'hidden', boxShadow: '0 1px 6px rgba(20,50,30,0.04)' }}>

          {/* Solution header */}
          <div style={{ backgroundColor: C.light, padding: '0.6rem 1rem', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, fontSize: '0.92rem', color: C.mid }}>Solution {sIdx + 1}</span>
            {list.length > 1 && (
              <button type="button" onClick={() => removeSolution(sIdx)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.red, fontSize: '0.78rem', fontWeight: 600 }}>
                Remove
              </button>
            )}
          </div>

          <div style={{ padding: '0.9rem 1rem' }}>
            {/* Solution Name */}
            <div style={{ marginBottom: '0.9rem' }}>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.textGr, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Solution Name
              </label>
              <input
                type="text"
                value={sol.solutionName || ''}
                onChange={e => updateSolution(sIdx, { solutionName: e.target.value })}
                placeholder="e.g. Preventive Solution"
                style={{ width: '55%', height: 36, borderRadius: 8, border: `1.5px solid ${C.border}`, padding: '0 0.75rem', fontSize: '0.87rem', outline: 'none', color: C.textDk, boxSizing: 'border-box', backgroundColor: C.white }}
              />
            </div>

            {/* Products label */}
            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.textGr, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Products
            </label>

            {/* Products table */}
            <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: C.dark }}>
                    <th style={{ ...thSt, width: 36, textAlign: 'center' }}>#</th>
                    <th style={thSt}>Product Name</th>
                    <th style={{ ...thSt, width: 160 }}>Dose</th>
                    <th style={{ ...thSt, width: 185 }}>Application Method</th>
                    <th style={{ ...thSt, width: 54, textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(sol.products || []).map((p, pIdx) => (
                    <tr key={pIdx} style={{ borderBottom: `1px solid ${C.border}`, backgroundColor: pIdx % 2 === 0 ? C.white : '#fafcfb' }}>
                      <td style={{ ...tdSt, textAlign: 'center' }}>
                        <span style={{ fontSize: '0.82rem', color: C.textGr, fontWeight: 600 }}>{pIdx + 1}</span>
                      </td>
                      <td style={tdSt}>
                        <Select
                          options={productOpts}
                          value={productOpts.find(o => o.value === String(p.productId)) || (p.productName ? { value: p.productId, label: p.productName } : null)}
                          onChange={opt => updateProduct(sIdx, pIdx, { productId: opt?.value || '', productName: opt?.label || '' })}
                          placeholder="Select product"
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={selectSt}
                        />
                      </td>
                      <td style={tdSt}>
                        <input
                          type="text"
                          value={p.dose || ''}
                          onChange={e => updateProduct(sIdx, pIdx, { dose: e.target.value })}
                          placeholder="e.g. 2 ml / L"
                          style={{ width: '100%', height: 34, borderRadius: 7, border: `1.5px solid ${C.border}`, padding: '0 0.65rem', fontSize: '0.84rem', outline: 'none', boxSizing: 'border-box', backgroundColor: C.white }}
                        />
                      </td>
                      <td style={tdSt}>
                        <select
                          value={p.applicationMethod || ''}
                          onChange={e => updateProduct(sIdx, pIdx, { applicationMethod: e.target.value })}
                          style={{ width: '100%', height: 34, borderRadius: 7, border: `1.5px solid ${C.border}`, padding: '0 0.5rem', fontSize: '0.84rem', outline: 'none', backgroundColor: C.white, cursor: 'pointer' }}
                        >
                          <option value="">Select method</option>
                          {APP_METHODS_LIST.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </td>
                      <td style={{ ...tdSt, textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => removeProduct(sIdx, pIdx)}
                          disabled={sol.products.length === 1}
                          title="Remove row"
                          style={{ background: 'none', border: 'none', cursor: sol.products.length === 1 ? 'not-allowed' : 'pointer', color: sol.products.length === 1 ? '#ccc' : C.red, fontSize: '1rem', lineHeight: 1, padding: 0 }}
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Add Product */}
            <button
              type="button"
              onClick={() => addProduct(sIdx)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: `1.5px dashed ${C.border}`, background: 'none', cursor: 'pointer', fontSize: '0.82rem', color: C.mid, fontWeight: 600 }}
            >
              + Add Product
            </button>
          </div>
        </div>
      ))}

      {/* Add Another Solution */}
      <button
        type="button"
        onClick={addSolution}
        style={{ width: '100%', padding: '0.65rem', borderRadius: 10, border: `2px solid ${C.dark}`, background: 'none', cursor: 'pointer', fontSize: '0.87rem', color: C.dark, fontWeight: 700 }}
      >
        + Add Another Solution
      </button>
    </div>
  );
};

export default SolutionsEditor;
